import { Component, LLMProvider, Tool, DashCompModelConfig, Message, T_RESPONSE } from "../types";
import { DEFAULT_REPORT_COMP_MODELS } from "./types";

/**
 * Get API key and model based on provider configuration
 */
export function getApiKeyAndModel(
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	modelConfig?: DashCompModelConfig
): { apiKey: string | undefined; model: string | undefined } {
	const providers = llmProviders || ['anthropic', 'gemini', 'openai', 'groq'];
	let apiKey: string | undefined;
	let model: string | undefined;

	if (modelConfig?.model) {
		model = modelConfig.model;
		const modelProvider = model.split('/')[0] as LLMProvider;

		if (modelProvider === 'anthropic') apiKey = anthropicApiKey;
		else if (modelProvider === 'gemini') apiKey = geminiApiKey;
		else if (modelProvider === 'openai') apiKey = openaiApiKey;
		else if (modelProvider === 'groq') apiKey = groqApiKey;
	} else {
		for (const provider of providers) {
			if (provider === 'anthropic' && anthropicApiKey) {
				apiKey = anthropicApiKey;
				model = DEFAULT_REPORT_COMP_MODELS.anthropic;
				break;
			} else if (provider === 'gemini' && geminiApiKey) {
				apiKey = geminiApiKey;
				model = DEFAULT_REPORT_COMP_MODELS.gemini;
				break;
			} else if (provider === 'openai' && openaiApiKey) {
				apiKey = openaiApiKey;
				model = DEFAULT_REPORT_COMP_MODELS.openai;
				break;
			} else if (provider === 'groq' && groqApiKey) {
				apiKey = groqApiKey;
				model = DEFAULT_REPORT_COMP_MODELS.groq;
				break;
			}
		}
	}

	// OpenRouter fail-open — mirror the script/agent flow. When no first-party
	// provider key resolved but an OpenRouter key is configured, route through
	// OpenRouter instead of bailing out. LLM._parseModel rewrites an anthropic/*
	// model to the OpenRouter slug (ANTHROPIC_API_KEY absent + OPENROUTER_API_KEY
	// present), so selecting the Anthropic default here makes the reroute fire.
	if (!apiKey && process.env.OPENROUTER_API_KEY) {
		apiKey = process.env.OPENROUTER_API_KEY;
		if (!model || !model.startsWith('anthropic/')) {
			model = DEFAULT_REPORT_COMP_MODELS.anthropic;
		}
	}

	return { apiKey, model };
}

/**
 * Format components for prompt
 */
export function formatComponentsForPrompt(components: Component[]): string {
	if (!components || components.length === 0) {
		return 'No components available';
	}
	components = components.filter(c => c.name !== 'MultiComponentContainer');
	return components
		.map((comp, idx) => {
			const keywords = comp.keywords ? comp.keywords.join(', ') : '';
			const propsPreview = comp.props ? JSON.stringify(comp.props, null, 2) : 'No props';
			return `${idx + 1}. ID: ${comp.id}
   Name: ${comp.name}
   Type: ${comp.type}
   Description: ${comp.description || 'No description'}
   Keywords: ${keywords}
   Props Structure: ${propsPreview}`;
		})
		.join('\n\n');
}

/**
 * Format tools for prompt
 */
export function formatToolsForPrompt(tools?: Tool[]): string {
	if (!tools || tools.length === 0) {
		return 'No external tools available.';
	}
	return tools
		.map((tool, idx) => {
			const paramsStr = Object.entries(tool.params || {})
				.map(([key, type]) => `${key}: ${type}`)
				.join(', ');
			return `${idx + 1}. ID: ${tool.id}
   Name: ${tool.name}
   Description: ${tool.description}
   Parameters: { ${paramsStr} }`;
		})
		.join('\n\n');
}

/**
 * Send REPORT_COMP_RES response message
 */
export function sendReportCompResponse(
	id: string,
	res: T_RESPONSE,
	sendMessage: (message: Message) => void,
	clientId?: string
): void {
	const response: Message = {
		id,
		type: 'REPORT_COMP_RES',
		from: { type: 'data-agent' },
		to: {
			type: 'runtime',
			id: clientId
		},
		payload: {
			...res
		}
	};

	sendMessage(response);
}
